import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Network, Plus, Copy, RefreshCw, Trash2, Pencil, Loader2,
  KeyRound, Globe, Ticket, Info, CheckCircle
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SUBMIT_URL = `${API}/it-tickets/submit`;

const copyText = (text, label) => {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copied`);
};

function SystemDialog({ system, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: system?.name || "",
    description: system?.description || "",
    base_url: system?.base_url || "",
    callback_url: system?.callback_url || "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (system) await axios.put(`${API}/systems/${system.id}`, form);
      else await axios.post(`${API}/systems`, form);
      toast.success(system ? "System updated" : "System added");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save system");
    } finally { setSaving(false); }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent aria-describedby={undefined} className="bg-[#0c0c0e] border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-mono">{system ? "Edit System" : "Connect New System"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <p className="text-xs text-zinc-400 mb-1">Name *</p>
            <Input value={form.name} onChange={set("name")} placeholder="e.g. Expense App"
              className="bg-black/50 border-white/10 text-white" data-testid="system-name-input" />
          </div>
          <div>
            <p className="text-xs text-zinc-400 mb-1">Description</p>
            <Textarea value={form.description} onChange={set("description")} rows={2}
              className="bg-black/50 border-white/10 text-white resize-none" data-testid="system-desc-input" />
          </div>
          <div>
            <p className="text-xs text-zinc-400 mb-1">App URL</p>
            <Input value={form.base_url} onChange={set("base_url")} placeholder="https://..."
              className="bg-black/50 border-white/10 text-white" data-testid="system-url-input" />
          </div>
          <div>
            <p className="text-xs text-zinc-400 mb-1">Callback URL (for reply delivery, optional)</p>
            <Input value={form.callback_url} onChange={set("callback_url")} placeholder="https://.../api/helpdesk-callback"
              className="bg-black/50 border-white/10 text-white" data-testid="system-callback-input" />
          </div>
          <div className="flex gap-2 pt-1">
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="system-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (system ? "Save Changes" : "Add System")}
            </Button>
            <Button variant="outline" className="border-white/10" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const ConnectedSystems = () => {
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // null | "new" | system object

  const fetchSystems = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/systems`);
      setSystems(res.data);
    } catch { toast.error("Failed to load systems"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchSystems(); }, [fetchSystems]);

  const regenerate = async (s) => {
    if (!window.confirm(`Regenerate API key for ${s.name}? The old key will stop working immediately.`)) return;
    try {
      await axios.post(`${API}/systems/${s.id}/regenerate-key`);
      toast.success("Key regenerated");
      fetchSystems();
    } catch { toast.error("Failed to regenerate key"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name}? Its API key will stop working.`)) return;
    try {
      await axios.delete(`${API}/systems/${s.id}`);
      toast.success("System removed");
      fetchSystems();
    } catch { toast.error("Failed to remove system"); }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-blue-400 animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="connected-systems-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-400">
          Each connected app submits tickets with its own API key — this server is the central admin for all of them.
        </p>
        <Button size="sm" onClick={() => setDialog("new")} className="bg-blue-600 hover:bg-blue-700" data-testid="add-system-btn">
          <Plus className="w-4 h-4 mr-1" /> Connect System
        </Button>
      </div>

      {/* Integration how-to */}
      <div className="bg-blue-500/5 border border-blue-500/10 rounded-lg p-3 text-xs text-zinc-400 space-y-1">
        <p className="flex items-center gap-1.5 text-blue-400 font-medium"><Info className="w-3.5 h-3.5" /> How apps connect</p>
        <p>POST <code className="text-blue-300 font-mono">{SUBMIT_URL}</code>
          <button onClick={() => copyText(SUBMIT_URL, "Submit URL")} className="ml-1.5 text-zinc-500 hover:text-white align-middle"><Copy className="w-3 h-3 inline" /></button>
        </p>
        <p>Header: <code className="text-blue-300 font-mono">X-Integration-Key: &lt;system API key&gt;</code></p>
        <p>Body: <code className="text-blue-300 font-mono">{`{"name","department","description","submitter_email","ticket_type"}`}</code></p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {systems.map(s => (
          <Card key={s.id} className="bg-[#0c0c0e] border-white/5" data-testid={`system-card-${s.slug}`}>
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-white font-medium flex items-center gap-2">
                    <Network className="w-4 h-4 text-blue-400" /> {s.name}
                  </p>
                  <p className="text-xs text-zinc-500 mt-0.5">{s.description || "—"}</p>
                </div>
                <span className="flex items-center gap-1 text-[10px] text-green-400 bg-green-500/10 border border-green-500/20 px-1.5 py-0.5 rounded-full">
                  <CheckCircle className="w-3 h-3" /> {s.status}
                </span>
              </div>

              {s.base_url && (
                <a href={s.base_url} target="_blank" rel="noreferrer"
                  className="text-xs text-blue-400 hover:underline flex items-center gap-1">
                  <Globe className="w-3 h-3" /> {s.base_url}
                </a>
              )}

              <div className="flex items-center gap-2 bg-black/50 border border-white/5 rounded px-2 py-1.5">
                <KeyRound className="w-3.5 h-3.5 text-yellow-400 shrink-0" />
                <code className="text-xs text-zinc-400 font-mono truncate flex-1">
                  sys-•••{s.api_key.slice(-6)}
                </code>
                <button onClick={() => copyText(s.api_key, "API key")}
                  className="text-zinc-500 hover:text-white" data-testid={`copy-key-${s.slug}`}>
                  <Copy className="w-3.5 h-3.5" />
                </button>
              </div>

              <div className="flex items-center justify-between text-xs text-zinc-500">
                <span className="flex items-center gap-1">
                  <Ticket className="w-3.5 h-3.5" /> {s.ticket_count} tickets ({s.open_tickets} open)
                </span>
                <div className="flex gap-1">
                  <button onClick={() => setDialog(s)} className="p-1.5 rounded hover:bg-white/10 text-zinc-400"
                    title="Edit" data-testid={`edit-system-${s.slug}`}><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => regenerate(s)} className="p-1.5 rounded hover:bg-white/10 text-zinc-400"
                    title="Regenerate key"><RefreshCw className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(s)} className="p-1.5 rounded hover:bg-red-500/10 text-red-400/70"
                    title="Remove"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {dialog && (
        <SystemDialog
          system={dialog === "new" ? null : dialog}
          onClose={() => setDialog(null)}
          onSaved={fetchSystems}
        />
      )}
    </div>
  );
};
